import { applicabilityProblems, requirementSourceProblems } from './requirement-source.js';
export const SCOPING_TREATMENTS = ['assessed', 'documented', 'excluded'];
export const STATIONS = ['scope', 'prepare', 'audit', 'operate', 'report'];
/** Returns problems with one manifest; empty when valid. */
export function validateProgramManifest(m) {
    const errs = [];
    if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(m.key))
        errs.push(`${m.key}: key must be lower-case kebab`);
    if (!m.label.trim() || !m.shortLabel.trim())
        errs.push(`${m.key}: label and shortLabel are required`);
    if (m.manifestVersion < 1)
        errs.push(`${m.key}: manifestVersion starts at 1`);
    if (m.readiness.engine === 'framework' || m.readiness.engine === 'ksi' || m.readiness.engine === 'objective') {
        if (!m.frameworkId)
            errs.push(`${m.key}: readiness engine ${m.readiness.engine} needs a frameworkId`);
    }
    if (m.readiness.engine === 'jurisdiction' && m.frameworkId)
        errs.push(`${m.key}: jurisdiction programs are not framework-bound`);
    for (const combo of m.profile.invalidCombinations ?? []) {
        const cols = Object.entries(combo.when);
        if (cols.length < 2)
            errs.push(`${m.key}: an invalid combination names at least two columns (one column is a column rule)`);
        for (const [col, v] of cols) {
            const rule = m.profile.columns[col];
            if (!rule || rule.rule === 'forbidden' || (rule.values && !rule.values.includes(v))) {
                errs.push(`${m.key}: invalid combination ${col}=${v} is not a value the column accepts`);
            }
        }
        if (!combo.message.trim())
            errs.push(`${m.key}: an invalid combination needs a message`);
    }
    // V108-8-15: key fields asked at add. A column key names a column the profile requires; choice values are that
    // column's values; text / date fields take no values; at most one of the two change rules.
    const asked = new Set();
    for (const o of m.onboarding.askOptions ?? []) {
        if (asked.has(o.key))
            errs.push(`${m.key}: askOptions lists ${o.key} twice`);
        asked.add(o.key);
        const input = o.input ?? 'choice';
        if (input === 'choice' && !o.values.length)
            errs.push(`${m.key}: choice field ${o.key} needs values`);
        if (input !== 'choice' && o.values.length)
            errs.push(`${m.key}: ${input} field ${o.key} takes no values`);
        if (o.newPeriodOnChange && o.lockedOnceAssessed)
            errs.push(`${m.key}: ${o.key} can't both start a new period and be locked`);
        if (['report_type', 'certification_class', 'path'].includes(o.key)) {
            const rule = m.profile.columns[o.key];
            if (!rule || rule.rule !== 'required')
                errs.push(`${m.key}: askOptions column ${o.key} must be a required profile column`);
            else if (rule.values && o.values.some((v) => !rule.values.includes(v.value)))
                errs.push(`${m.key}: askOptions ${o.key} offers a value the column refuses`);
            if (input !== 'choice')
                errs.push(`${m.key}: column ${o.key} is asked as a choice`);
        }
        else if (!m.profile.options)
            errs.push(`${m.key}: askOptions option ${o.key} needs profile.options`);
    }
    if (m.readiness.legacyEngine !== undefined || m.readiness.switchRuleSet !== undefined) {
        if (!m.readiness.legacyEngine || !m.readiness.switchRuleSet)
            errs.push(`${m.key}: a readiness switch needs both legacyEngine and switchRuleSet`);
        if (m.readiness.legacyEngine === m.readiness.engine)
            errs.push(`${m.key}: legacyEngine must differ from engine`);
        if (m.readiness.switchRuleSet && !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(m.readiness.switchRuleSet))
            errs.push(`${m.key}: switchRuleSet must be lower-case kebab`);
        if (m.readiness.switchRuleSet && m.scoring.rulesRef && m.scoring.rulesRef !== m.readiness.switchRuleSet) {
            errs.push(`${m.key}: one approved rule set per program, so switchRuleSet and scoring.rulesRef must name the same family`);
        }
    }
    if (m.readiness.machineValidation) {
        if (m.readiness.objectiveMode !== 'objectives')
            errs.push(`${m.key}: machineValidation needs objectiveMode 'objectives'`);
        if (!m.features.persistentValidation)
            errs.push(`${m.key}: machineValidation needs features.persistentValidation`);
    }
    if (m.readiness.scheduledEvaluation) {
        if (m.readiness.machineValidation)
            errs.push(`${m.key}: scheduledEvaluation and machineValidation are exclusive (one writer per cell, ADR-0068 §3)`);
        if (m.readiness.engine !== 'objective' || m.readiness.objectiveMode !== 'objectives')
            errs.push(`${m.key}: scheduledEvaluation needs the objective engine and objectiveMode 'objectives'`);
    }
    if (m.readiness.engine === 'objective' && m.readiness.objectiveMode !== 'objectives') {
        errs.push(`${m.key}: objective engine needs objectiveMode 'objectives'`);
    }
    errs.push(...requirementSourceProblems(m));
    errs.push(...applicabilityProblems(m));
    if (!!m.features.transitionGuidance !== !!m.transition)
        errs.push(`${m.key}: features.transitionGuidance and transition go together`);
    if (m.transition?.notice === 'scn' && !m.features.significantChangeNotices)
        errs.push(`${m.key}: transition notice scn needs features.significantChangeNotices`);
    if (m.features.alternativeFrameworkMaterials && m.profile.columns.certification_class?.rule !== 'required')
        errs.push(`${m.key}: alternativeFrameworkMaterials needs a required certification_class column`);
    if (m.period.model === 'none' && m.period.maxMonths != null)
        errs.push(`${m.key}: a program without periods has no maxMonths`);
    if (m.period.model !== 'none' && (m.period.maxMonths == null || m.period.maxMonths < 1)) {
        errs.push(`${m.key}: period model ${m.period.model} needs maxMonths ≥ 1`);
    }
    if (m.stage.model === 'standing' && m.period.model !== 'none')
        errs.push(`${m.key}: standing programs have no periods`);
    if (m.scoring.engine !== 'none' && !m.scoring.rulesRef)
        errs.push(`${m.key}: scoring engine ${m.scoring.engine} needs rulesRef`);
    if (m.features.poam && !m.scoring.rulesRef)
        errs.push(`${m.key}: features.poam needs scoring.rulesRef`);
    if (m.features.poam && m.period.model === 'none')
        errs.push(`${m.key}: POA&M items are per period; a program without periods cannot use them`);
    if (m.operate.contributors.includes('scn') && !m.features.significantChangeNotices) {
        errs.push(`${m.key}: scn contributor needs features.significantChangeNotices`);
    }
    if (m.operate.contributors.includes('acceptances') && m.acceptanceSources.length === 0) {
        errs.push(`${m.key}: acceptances contributor needs acceptanceSources`);
    }
    if (!m.operate.queue && m.operate.contributors.length)
        errs.push(`${m.key}: contributors without an operate queue`);
    if (m.defaultForUntaggedWeakness && !m.acceptanceSources.includes('accepted_weakness')) {
        errs.push(`${m.key}: defaultForUntaggedWeakness needs accepted_weakness in acceptanceSources`);
    }
    for (const s of Object.keys(m.shell.notApplicable)) {
        if (!STATIONS.includes(s))
            errs.push(`${m.key}: unknown station ${s}`);
    }
    if (m.shell.defaultTab !== 'stage' && m.shell.notApplicable[m.shell.defaultTab]) {
        errs.push(`${m.key}: default tab ${m.shell.defaultTab} is not applicable`);
    }
    for (const [s, id] of Object.entries(m.shell.panels)) {
        if (!STATIONS.includes(s))
            errs.push(`${m.key}: unknown panel station ${s}`);
        if (typeof id !== 'string' || !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(id))
            errs.push(`${m.key}: panel id for ${s} must be lower-case kebab`);
    }
    if (m.soa) {
        const src = m.requirementSource;
        if (m.readiness.engine !== 'objective')
            errs.push(`${m.key}: soa needs the objective engine`);
        if (!m.soa.refPrefix.trim())
            errs.push(`${m.key}: soa.refPrefix is required`);
        if (!src || !('kind' in src) || src.kind !== 'framework' || src.programControls?.refPrefix !== m.soa.refPrefix) {
            errs.push(`${m.key}: soa rules on program controls, so requirementSource must be a framework leaf with programControls.refPrefix = soa.refPrefix`);
        }
        if (!m.packages.kinds.includes(m.soa.packageKind))
            errs.push(`${m.key}: soa.packageKind must be one of packages.kinds`);
    }
    if (m.certificationCycle) {
        const f = m.certificationCycle.rulesRef;
        if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(f))
            errs.push(`${m.key}: certificationCycle.rulesRef must be lower-case kebab`);
        if ((m.scoring.rulesRef && m.scoring.rulesRef !== f) || (m.readiness.switchRuleSet && m.readiness.switchRuleSet !== f)) {
            errs.push(`${m.key}: one approved rule set per program, so certificationCycle.rulesRef must match scoring.rulesRef / switchRuleSet when set`);
        }
        if (m.period.model === 'none')
            errs.push(`${m.key}: a certification cycle needs periods`);
    }
    if (m.scopeView && m.scopeView.source !== 'boundary')
        errs.push(`${m.key}: scopeView.source must be boundary`);
    if (m.scopeView && m.period.model === 'none')
        errs.push(`${m.key}: a boundary scope view is per program and period; a program without periods has none`);
    if (m.scoping) {
        const cats = m.scoping.categories;
        if (!cats.length)
            errs.push(`${m.key}: scoping needs at least one category`);
        const seen = new Set();
        for (const c of cats) {
            if (!/^[a-z0-9]+(_[a-z0-9]+)*$/.test(c.key))
                errs.push(`${m.key}: scoping category key ${c.key} must be lower snake case`);
            if (seen.has(c.key))
                errs.push(`${m.key}: duplicate scoping category ${c.key}`);
            seen.add(c.key);
            if (!c.label.trim())
                errs.push(`${m.key}: scoping category ${c.key} needs a label`);
            if (!SCOPING_TREATMENTS.includes(c.treatment))
                errs.push(`${m.key}: scoping category ${c.key} has unknown treatment ${String(c.treatment)}`);
        }
        if (cats.length && !cats.some((c) => c.treatment === 'assessed'))
            errs.push(`${m.key}: scoping needs an assessed category`);
        if (m.period.model === 'none')
            errs.push(`${m.key}: scoping is per period; a program without periods cannot scope assets`);
    }
    if (m.resourceScope) {
        if (m.scoping)
            errs.push(`${m.key}: resourceScope is the default for programs without scoping categories`);
        if (m.period.model === 'none' && m.resourceScope === 'in_boundary_resources')
            errs.push(`${m.key}: resourceScope is per period; a program without periods cannot scope assets`);
    }
    for (const [col, r] of Object.entries(m.profile.columns)) {
        if (r?.rule === 'forbidden' && r.values)
            errs.push(`${m.key}: forbidden column ${col} lists values`);
    }
    return errs;
}
/** Registry-wide problems (duplicates, unique flags). */
export function validateProgramRegistry(all) {
    const errs = all.flatMap(validateProgramManifest);
    const keys = new Set();
    const orders = new Set();
    for (const m of all) {
        if (keys.has(m.key))
            errs.push(`duplicate program key ${m.key}`);
        keys.add(m.key);
        if (orders.has(m.order))
            errs.push(`duplicate order ${m.order} (${m.key})`);
        orders.add(m.order);
    }
    if (all.filter((m) => m.defaultForUntaggedWeakness).length > 1)
        errs.push('more than one program claims untagged weaknesses');
    return errs;
}
//# sourceMappingURL=manifest.js.map